import React from "react";
import { motion } from "framer-motion";
import BarChart from "./BarChart";
import Dropdown from "./Dropdown";

const Vision = () => {
  const chartData = [
    {
      text: "How many years of experience do you possess in the field of medicine?",
      series: [24.8, 16.2, 16.2, 42.8],
      labels: ["1-3 years", "3-5 years", "5-10 years", "10+ years"],
    },
    {
      text: "When a patient visits you, how much time you invest in initial investigation/symptom investigation?",
      series: [4.8, 38.1, 39, 18.1],
      labels: ["1-3 minutes", "3-5 minutes", "5-7 minutes", "7-9 minutes"],
    },
    {
      text: "Do you think a junior doctor can save your time if they could have performed initial symptom investigation?",
      series: [73.8, 20, 6.2],
      labels: ["Yes", "Maybe", "No"],
    },
    {
      text: "How much time, the assistant can save for each patient (on an average)?",
      series: [1.9, 4.8, 3.8, 25.7, 45.7, 18.1],
      labels: [
        "0",
        "30 seconds",
        "1 minute",
        "1-2 minutes",
        "2-3 minutes",
        "3-4 minutes",
      ],
    },
  ];

  const publicationData = {
    Patent: [
      "System and Method for a Knowledge-Enhanced Multi-Modal Symptom Investigation and Disease Diagnosis Assistant, Jan 2024 (Granted)",
      "System and Method for Automated Disease Diagnosis, March 2023, (Filed & Published)",
    ],
    Conference: [
      "From Sights to Insights: Towards Summarization of Multimodal Clinical Documents - ACL 2024",
      "Seeing is believing! Towards Knowledge-Infused Multi-modal Medical Dialogue Generation, COLING 2024",
      "Knowledge Infused Multi-modal Clinical Conversation Summarization - CIKM 2023",
    ],
    Journal: [
      "Knowledge-Infused Automated Disease Diagnosis Assistant, Nature 2024",
      "Symptom Investigation and Diagnosis Assistant, IEEE AI 2023",
    ],
  };

  // Animation variants
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.2 },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 40 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <motion.div
      variants={container}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true }}
      className="w-full bg-white dark:bg-black py-16 px-4 md:px-16 lg:px-32"
    >
      {/* HEADER */}
      <motion.div variants={item} className="text-center mt-20 max-w-4xl mx-auto">
        <h1 className="text-3xl md:text-5xl font-semibold text-black dark:text-white">
          Doctor Insights & AI Assistant Impact
        </h1>

        <p className="mt-6 text-gray-600 dark:text-gray-400 text-base md:text-lg">
          We analyzed feedback from 100+ doctors across institutions like AIIMS
          to understand workload challenges and explore how AI can assist in
          faster and more efficient patient handling.
        </p>
      </motion.div>

      {/* CHARTS */}
      <motion.div
        variants={container}
        className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-8"
      >
        {chartData.map((data, index) => (
          <motion.div
            key={index}
            variants={item}
            whileHover={{ scale: 1.03 }}
            className="bg-[#f5f9ff] dark:bg-gray-900 p-6 rounded-xl shadow-md"
          >
            <h2 className="text-lg md:text-xl font-semibold text-center text-[#1966E8] mb-6">
              {data.text}
            </h2>
            <BarChart series={data.series} labels={data.labels} />
          </motion.div>
        ))}
      </motion.div>

      {/* FRAMEWORK IMAGE */}
      <motion.div variants={item} className="mt-20 text-center">
        <h2 className="text-2xl md:text-3xl font-semibold text-black dark:text-white">
          AI-Based Diagnosis Framework
        </h2>

        <img
          src="./img/SQ_5.png"
          alt="framework"
          className="mt-8 mx-auto rounded-xl shadow-lg border p-4 bg-white"
        />
      </motion.div>

      {/* DESCRIPTION */}
      <motion.p
        variants={item}
        className="mt-10 max-w-3xl mx-auto text-gray-600 dark:text-gray-400 text-center"
      >
        Our conversational AI assistant acts as the first point of contact,
        performing initial symptom assessments and preparing structured reports
        for doctors — saving valuable consultation time and improving efficiency.
      </motion.p>

      {/* RESEARCH SECTION */}
      <motion.div variants={item} className="mt-20 text-center">
        <h2 className="text-2xl md:text-3xl font-semibold text-black dark:text-white">
          Research & Development
        </h2>

        <p className="mt-4 text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
          Built over two years, our system is backed by patents and publications
          in top-tier conferences and journals.
        </p>
      </motion.div>

      {/* DROPDOWNS */}
      <motion.div
        variants={container}
        className="mt-10 max-w-3xl mx-auto"
      >
        {Object.entries(publicationData).map(([title, content], index) => (
          <motion.div key={index} variants={item}>
            <Dropdown title={title} content={content} />
          </motion.div>
        ))}
      </motion.div>
    </motion.div>
  );
};

export default Vision;